// Complete updated landing page with persistent theme using localStorage and system preference fallback

// src/pages/public/Landing.jsx
import React, { useState, useMemo } from 'react';
import StarParticles from "../components/StarParticles"; 
import Services from "../sections/Services"; 
import { Link } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Grid, 
  Paper,
  Container,
  IconButton,
  CssBaseline,
  AppBar,
  createTheme,
  ThemeProvider
} from '@mui/material';
import { 
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon
} from '@mui/icons-material'; 
import { Stack } from "@mui/material";
import { motion } from 'framer-motion';
import './waves.css';
import SendIcon from '@mui/icons-material/Send';
import SecurityIcon from '@mui/icons-material/Security';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupsIcon from '@mui/icons-material/Groups';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import PublicIcon from '@mui/icons-material/Public';
import StarIcon from '@mui/icons-material/Star';
import MenuIcon from '@mui/icons-material/Menu';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

// ✅ Import your bank background image
import bankBg from "../../assets/bank.jpg";


export default function Landing() {
  const getPreferredTheme = () => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) return savedTheme;
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    return prefersDark ? 'dark' : 'light';
  };

  const [mode, setMode] = useState(getPreferredTheme);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const toggleTheme = () => {
    const newMode = mode === 'dark' ? 'light' : 'dark';
    setMode(newMode);
    localStorage.setItem('theme', newMode);
  };

  const theme = useMemo(() => createTheme({
    palette: {
      mode,
      primary: { main: '#00B4D8', dark: '#0F172A' },
      background: {
        default: mode === 'light' ? '#F8FAFC' : '#0D1117',
        paper: mode === 'light' ? '#ffffff' : '#161B22'
      },
    },
    typography: {
      fontFamily: ['Poppins', 'Inter', 'Arial', 'sans-serif'].join(',')
    }
  }), [mode]);

  const navLinks = [
    { label: 'Home', href: '#home' },
    { label: 'Features', href: '#features' },
    { label: 'Statistics', href: '#statistics' },
    { label: 'FAQ', href: '#faq' },
    { label: 'Contact', href: '#contact' },
  ];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ position: 'relative', overflow: 'hidden', minHeight: '100vh' }}>
        {/* AppBar */}
        <AppBar position="fixed" elevation={0} sx={{ backgroundColor: theme.palette.background.paper, color: 'inherit', zIndex: 10 }}>
          <Container maxWidth="lg" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Box sx={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg, #00B4D8 60%, #0F172A 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="white" fontWeight="bold">FB</Typography>
              </Box>
              <Typography variant="h6" fontWeight="bold">Fentrixbank</Typography>
            </Box>

            {/* Desktop Nav */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 3, alignItems: 'center' }}>
              {navLinks.map((link, i) => (
                <Typography key={i} variant="body1" component="a" href={link.href} sx={{ textDecoration: 'none', color: 'inherit', whiteSpace: 'nowrap', '&:hover': { color: '#00B4D8' } }}>{link.label}</Typography>
              ))}
              <IconButton onClick={toggleTheme} color="inherit" sx={{ ml: 1 }}>
                {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
              </IconButton>
              <Button variant="contained" size="small" href="/register" style={{ background: 'linear-gradient(135deg, #00B4D8, #7B2FF2)', color: 'white', borderRadius: '15px', fontSize: '13px' }}>Open Account</Button>
            </Box>

            {/* Mobile Nav */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
              <IconButton onClick={() => setDrawerOpen(true)} color="inherit">
                <MenuIcon />
              </IconButton>
            </Box>
          </Container>
        </AppBar>

        {/* Mobile Drawer */}
        <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
          <Box sx={{ width: 250, pt: 2 }}>
            <List>
              {navLinks.map((link, i) => (
                <ListItemButton key={i} component="a" href={link.href} onClick={() => setDrawerOpen(false)}>
                  <ListItemText primary={link.label} />
                </ListItemButton>
              ))}
              <ListItemButton onClick={toggleTheme}>
                <ListItemIcon>
                  {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
                </ListItemIcon>
                <ListItemText primary={mode === 'dark' ? 'Light Mode' : 'Dark Mode'} />
              </ListItemButton>
              <ListItemButton component="a" href="/register" onClick={() => setDrawerOpen(false)}>
                <ListItemText primary="Open Account" />
              </ListItemButton>
            </List>
          </Box>
        </Drawer>

        {/* ✅ Hero Section with bank.jpg background */}
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            minHeight: "88vh",
            pt: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            backgroundImage: `url(${bankBg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          {/* Overlay for readability */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: mode === "dark" ? "rgba(0,0,0,0.55)" : "rgba(255,255,255,0.55)",
              zIndex: 1,
            }}
          />

          {/* Hero Content */}
          <Container maxWidth="md" sx={{ position: "relative", zIndex: 2 }}>
            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
              <Typography
                variant="h3"
                fontWeight="bold"
                gutterBottom
                sx={{
                  fontSize: { xs: "1.8rem", sm: "2.2rem", md: "3rem" },
                  lineHeight: 1.2,
                }}
              >
                Welcome to Fentrixbank
              </Typography>
              <Typography
                variant="h6"
                mb={4}
                sx={{
                  fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" },
                  maxWidth: "700px",
                  mx: "auto",
                }}
              >
                Your all-in-one digital bank — fast, secure, and built for your lifestyle.
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  variant="contained"
                  href="/register"
                  sx={{
                    background: "linear-gradient(135deg, #00B4D8, #7B2FF2)",
                    color: "white",
                    borderRadius: "15px",
                    fontSize: { xs: "14px", sm: "16px" },
                    px: 3,
                  }}
                >
                  Get Started
                </Button>
                <Button
                  variant="outlined"
                  href="/login"
                  sx={{
                    borderRadius: "15px",
                    fontSize: { xs: "14px", sm: "16px" },
                    color: "#00B4D8",
                    borderColor: "#00B4D8",
                    px: 3,
                  }}
                >
                  Login
                </Button>
              </Box>
            </motion.div>
          </Container>
        </Box>

{/* ===== Redesigned Features Section (Cinematic Banking Style) ===== */}
<Box
  component="section"
  sx={{
    position: "relative",
    overflow: "hidden",
    py: { xs: 10, md: 16 },
    background: "linear-gradient(180deg, #0a1625 0%, #000 100%)",
    color: "#fff",
  }}
>
  <style>{`
    @keyframes fsFloat {
      0% { transform: translateY(0) scale(1); opacity: .08; }
      50% { transform: translateY(-14px) scale(1.03); opacity: .14; }
      100% { transform: translateY(0) scale(1); opacity: .08; }
    }

    @keyframes fsSweep {
      0% { transform: translateX(-120%); opacity: 0; }
      50% { transform: translateX(0%); opacity: .14; }
      100% { transform: translateX(120%); opacity: 0; }
    }

    @keyframes fsPulse {
      0% { stroke-opacity: .06; }
      50% { stroke-opacity: .22; }
      100% { stroke-opacity: .06; }
    }
  `}</style>

  {/* ---- Shimmer Sweep Background ---- */}
  <Box
    sx={{
      position: "absolute",
      inset: 0,
      zIndex: 0,
      pointerEvents: "none",
    }}
  >
    <Box
      sx={{
        position: "absolute",
        top: "20%",
        left: "-35%",
        width: "150%",
        height: { xs: 100, md: 180 },
        background:
          "linear-gradient(90deg, rgba(0,180,216,0), rgba(0,180,216,0.18), rgba(0,180,216,0))",
        transform: "skewX(-12deg)",
        filter: "blur(24px)",
        animation: "fsSweep 14s linear infinite",
        opacity: 0.08,
      }}
    />
  </Box>

  {/* ---- Floating Neon Blobs ---- */}
  <Box
    sx={{
      position: "absolute",
      inset: 0,
      zIndex: 0,
      pointerEvents: "none",
    }}
  >
    <Box
      sx={{
        position: "absolute",
        top: "8%",
        left: "4%",
        width: 90,
        height: 90,
        borderRadius: "50%",
        background:
          "radial-gradient(circle, rgba(0,180,216,0.15), rgba(0,180,216,0.04))",
        filter: "blur(12px)",
        animation: "fsFloat 7s ease-in-out infinite",
      }}
    />
    <Box
      sx={{
        position: "absolute",
        top: "32%",
        right: "6%",
        width: 70,
        height: 70,
        borderRadius: "50%",
        background:
          "radial-gradient(circle, rgba(255,78,197,0.15), rgba(255,78,197,0.04))",
        filter: "blur(12px)",
        animation: "fsFloat 9s ease-in-out infinite",
      }}
    />
  </Box>

  {/* ---- Arc Lines ---- */}
  <Box
    sx={{
      position: "absolute",
      inset: 0,
      pointerEvents: "none",
      zIndex: 0,
    }}
  >
    <svg width="100%" height="100%" viewBox="0 0 1200 600" style={{ opacity: 0.08 }}>
      <defs>
        <linearGradient id="fs1" x1="0" x2="1">
          <stop offset="0%" stopColor="#00B4D8" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#FF4EC5" stopOpacity="0.3" />
        </linearGradient>
        <linearGradient id="fs2" x1="0" x2="1">
          <stop offset="0%" stopColor="#FF4EC5" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#00B4D8" stopOpacity="0.3" />
        </linearGradient>
      </defs>

      <path
        d="M80 480 C280 300, 520 250, 800 160"
        stroke="url(#fs1)"
        strokeWidth="2"
        fill="none"
        style={{ animation: "fsPulse 7s ease-in-out infinite" }}
      />
      <path
        d="M200 540 C400 350, 700 300, 1000 180"
        stroke="url(#fs2)"
        strokeWidth="1.6"
        fill="none"
        style={{ animation: "fsPulse 9s ease-in-out infinite" }}
      />
    </svg>
  </Box>

  {/* ================= MAIN CONTENT ================= */}
  <Container sx={{ position: "relative", zIndex: 2, maxWidth: 1500 }}>
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
    >
      <Typography
        variant="h4"
        textAlign="center"
        fontWeight="bold"
        sx={{
          mb: 8,
          background: "linear-gradient(90deg, #00B4D8, #FF4EC5)",
          WebkitBackgroundClip: "text",
          color: "transparent",
        }}
      >
        Powerful Banking Features
      </Typography>

      <Grid container spacing={{ xs: 3, md: 4 }}>
        {[
          ["Instant Transfers", "Send and receive money instantly worldwide.", <SendIcon />],
          ["Secure Banking", "Military-grade encryption for ultimate protection.", <SecurityIcon />],
          ["Card Management", "Freeze, replace, and manage your card effortlessly.", <CreditCardIcon />],
          ["24/7 Support", "Real humans. Real help. Anytime.", <SupportAgentIcon />],
        ].map(([title, desc, icon], i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              whileHover={{ y: -6 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  height: "100%",
                  background:
                    "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))",
                  border: "1px solid rgba(255,255,255,0.05)",
                  backdropFilter: "blur(12px)",
                  position: "relative",
                  overflow: "hidden",
                  transition: "0.3s ease",
                  "&:hover": {
                    boxShadow:
                      "0 12px 40px rgba(0,180,216,0.25), 0 12px 40px rgba(255,78,197,0.22)",
                  },
                }}
              >
                {/* Icon orb */}
                <Box
                  sx={{
                    width: 70,
                    height: 70,
                    mx: "auto",
                    mb: 2,
                    borderRadius: "50%",
                    background:
                      "linear-gradient(140deg, #00B4D8, #FF4EC5)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow:
                      "0 0 22px rgba(0,180,216,0.35), 0 0 26px rgba(255,78,197,0.25)",
                    color: "#fff",
                  }}
                >
                  {icon}
                </Box>

                <Typography
                  variant="h6"
                  textAlign="center"
                  fontWeight="bold"
                  sx={{ color: "#fff", mb: 1 }}
                >
                  {title}
                </Typography>

                <Typography
                  variant="body2"
                  textAlign="center"
                  sx={{ color: "rgba(220,220,220,0.7)" }}
                >
                  {desc}
                </Typography>
              </Paper>
            </motion.div>
          </Grid>
        ))}
      </Grid>
    </motion.div>
  </Container>
</Box> 

                      <Services />

        {/* Statistics Section */} 
<Box sx={{ position: 'relative', py: 10, overflow: 'hidden' }}>
  <Container
    maxWidth={false}
    disableGutters
    sx={{
      position: 'relative',
      zIndex: 1,
      px: { xs: 2, sm: 3, md: 4 }, // margin from screen edge
    }}
  >
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 1 }}
    >
      <Typography
        variant="h4"
        textAlign="center"
        fontWeight="bold"
        color="white"
        mb={6}
      >
        Our Global Impact
      </Typography>

      <Grid
        container
        spacing={{ xs: 2, md: 3 }}
        sx={{
          width: '100%',
          margin: 0,
        }}
      >
        {[
          ["Users Worldwide", "1M+", <GroupsIcon />],
          ["Transactions Processed", "25M+", <CurrencyExchangeIcon />],
          ["Countries Supported", "90+", <PublicIcon />],
          ["Customer Satisfaction", "99.9%", <StarIcon />]
        ].map(([label, value, icon], i) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={4} // 3 per row on desktop
            key={i}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              width: '100%',
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                p: { xs: 2, sm: 3 },
                borderRadius: 4,
                bgcolor: '#1e293b',
                color: 'white',
                boxShadow: '0 0 20px rgba(0, 212, 255, 0.2)',
                textAlign: 'center',
                minHeight: { xs: 140, sm: 200 },
                alignItems: 'center',
                justifyContent: 'center',
                transition: '0.3s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 0 30px rgba(0, 212, 255, 0.4)',
                },
              }}
            >
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  mb: 2,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, #00B4D8, #FF4EC5)',
                  color: 'white',
                }}
              >
                {icon}
              </Box>
              <Typography variant="h5" fontWeight="bold">
                {value}
              </Typography>
              <Typography variant="body2" color="gray">
                {label}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </motion.div>
  </Container>
</Box>

        {/* FAQ Section */}
        <Container id="faq" maxWidth="md" sx={{ py: 10 }}>
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 1 }}>
            <Typography variant="h4" textAlign="center" fontWeight="bold" mb={6}>
              Frequently Asked Questions
            </Typography>
            {[
              ["Is FentrixBank safe to use?", "Absolutely. We use top-grade encryption and security practices to protect your data."],
              ["How ho I open an account?", "Just click 'Open Account' and fill in the required details. It takes less than 3 minutes!"],
              ["Are there any hidden fees?", "No. FentrixBank is transparent with all fees upfront."],
              ["Can I access my account abroad?", "Yes. FentrixBank is available globally, anytime, anywhere."]
            ].map(([q, a], i) => (
              <Accordion key={i} sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="bold">{q}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>{a}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </motion.div>
        </Container>

        {/* Contact Section */}
        <Container id="contact"    maxWidth="md" sx={{ py: 10 }}>
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 1 }}>
            <Typography variant="h4" textAlign="center" fontWeight="bold" mb={6}>
              Contact Us
            </Typography>
            <Grid container spacing={4}>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={2}>
                  <PhoneIcon color="primary" />
                  <Typography>+1 816 556 6378</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={2} mt={2}>
                  <EmailIcon color="primary" />
                  <Typography>info@fentrixbank.org</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={2} mt={2}>
                  <LocationOnIcon color="primary" />
                  <Typography>Kansas City, Mo, USA</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  <Typography>
                    We’d love to hear from you. Reach out to us via any channel and our team will get back shortly.
                  </Typography>
                  <Button variant="contained" sx={{ mt: 3 }}>Send Message</Button>
                </Box>
              </Grid>
            </Grid>
          </motion.div>
        </Container>

{/* Footer */}
<Box sx={{ bgcolor: theme.palette.background.paper, py: 6 }}>
  <Container maxWidth="lg">
    <Grid
      container
      spacing={4}
      justifyContent="space-between"
      alignItems="center"
    >
      {/* Left */}
      <Grid item xs={12} md={6}>
        <Typography variant="h6" fontWeight="bold">
          FentrixBank
        </Typography>

        <Typography variant="body2" color="text.secondary" mt={1}>
          © {new Date().getFullYear()} FentrixBank. All rights reserved.
        </Typography>
      </Grid>

      {/* Right Navigation */}
      <Grid
        item
        xs={12}
        md={6}
        textAlign={{ xs: "left", md: "right" }}
      >
        <Stack
          direction="row"
          spacing={3}
          justifyContent={{ xs: "flex-start", md: "flex-end" }}
        >
          {[
            { name: "Privacy Policy", path: "/privacy" },
            { name: "Terms of Service", path: "/terms" },
            { name: "Support", path: "/support" },
            { name: "KYC / AML Policy", path: "/kyc-aml" },
            { name: "Security", path: "/security" }
          ].map((link, i) => (
            <Typography
              key={i}
              variant="body2"
              component={Link}
              to={link.path}
              sx={{
                textDecoration: "none",
                color: "inherit",
                cursor: "pointer",
                "&:hover": { color: "#00B4D8" }
              }}
            >
              {link.name}
            </Typography>
          ))}
        </Stack>
      </Grid>
    </Grid>
  </Container>
</Box>
      </Box>
    </ThemeProvider>
  );
}
